import { ArrowUpRight, Check } from "lucide-react";

import { AppLaunchButton, AppMark } from "@/components/kb/app-mark";
import { GameLine } from "@/components/kb/game-line";
import { LeagueBadge } from "@/components/kb/league-badge";
import { ScoreboardTile } from "@/components/kb/scoreboard-tile";
import { Button } from "@/components/ui/button";
import type { Notification, Stat } from "@/lib/api";
import { demoGame } from "@/lib/demo";
import { featuredApps } from "@/lib/featured-apps";
import { statAbbr } from "@/lib/rule-display";
import { cn } from "@/lib/utils";

function formatTime(value: string): string {
	const date = new Date(value);

	if (Number.isNaN(date.getTime())) {
		return value;
	}

	return new Intl.DateTimeFormat(undefined, {
		hour: "numeric",
		minute: "2-digit",
	}).format(date);
}

function notificationStat(notification: Notification): Stat | null {
	const stat = notification.stat ?? notification.data.stat;

	return stat === "avg" ||
		stat === "hr" ||
		stat === "rbi" ||
		stat === "obp" ||
		stat === "slg"
		? stat
		: null;
}

function displayValue(value: unknown): string {
	if (value === null || value === undefined || value === "") {
		return "—";
	}

	if (typeof value === "number") {
		return Number.isInteger(value) ? String(value) : value.toFixed(3);
	}

	return String(value);
}

export function NotificationCard({
	notification,
	onMarkRead,
	markingRead,
	className,
}: {
	notification: Notification;
	onMarkRead?: () => void;
	markingRead?: boolean;
	className?: string;
}) {
	const unread = !notification.readAt;
	const stat = notificationStat(notification);
	const observed =
		notification.observedValue ?? notification.data.observedValue;
	const target =
		notification.targetValue ??
		notification.data.targetValue ??
		notification.data.value;
	const hasObserved = observed !== null && observed !== undefined;
	const game = notification.game ?? demoGame(notification.gamePk);
	const gameIsDemo = !notification.game;
	const apps = (notification.apps ?? [])
		.map((id) => featuredApps.find((app) => app.id === id))
		.filter((app) => app !== undefined);

	return (
		<article
			data-notification-id={notification.id}
			data-testid="notification-card"
			className={cn(
				"flex gap-4 rounded-xl border bg-card p-4 shadow-xs sm:p-5",
				unread && "border-signal/60",
				className,
			)}
		>
			<ScoreboardTile
				abbr={hasObserved ? statAbbr(stat) : `${statAbbr(stat)} target`}
				lit={unread}
				value={displayValue(hasObserved ? observed : target)}
			/>
			<div className="flex min-w-0 flex-1 flex-col gap-3">
				<div className="flex items-start justify-between gap-3">
					<h3
						className={cn(
							"text-[0.9375rem] leading-snug",
							unread ? "font-semibold" : "font-medium",
						)}
					>
						{notification.message}
					</h3>
					<div className="flex shrink-0 items-center gap-1.5 pt-0.5">
						<time
							className="type-data text-muted-foreground"
							dateTime={notification.createdAt}
						>
							{formatTime(notification.createdAt)}
						</time>
						{unread && (
							<span className="size-1.5 rounded-full bg-signal">
								<span className="sr-only">Unread</span>
							</span>
						)}
						{unread && onMarkRead && (
							<Button
								aria-label="Mark notification as read"
								data-testid="mark-read"
								disabled={markingRead}
								onClick={onMarkRead}
								size="icon-xs"
								variant="ghost"
							>
								<Check />
							</Button>
						)}
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
					<LeagueBadge league={notification.league ?? "MLB"} />
					<span className="type-data text-muted-foreground">
						{notification.title}
					</span>
					<GameLine demo={gameIsDemo} game={game} />
				</div>
				{apps.length > 0 && (
					<div className="flex flex-wrap gap-2" data-testid="notification-apps">
						{apps.map((app, index) => (
							<AppLaunchButton
								app={app}
								key={app.id}
								primary={unread && index === 0}
							/>
						))}
					</div>
				)}
			</div>
		</article>
	);
}

/** Phone lock-screen mock used while composing a rule. */
export function PushPreview({
	headline,
	detail,
	apps = [],
	className,
}: {
	headline: string;
	detail: string;
	apps?: string[];
	className?: string;
}) {
	const previewApps = apps
		.map((id) => featuredApps.find((app) => app.id === id))
		.filter((app) => app !== undefined)
		.slice(0, 2);

	return (
		<div
			className={cn(
				"relative overflow-hidden rounded-[1.4rem] bg-device p-3 text-device-foreground shadow-lg ring-1 ring-white/10 backdrop-blur",
				className,
			)}
		>
			<div className="flex items-center gap-2">
				<span className="flex size-5 items-center justify-center rounded-[0.3rem] bg-signal font-display text-[0.6875rem] font-bold text-signal-foreground">
					K
				</span>
				<span className="text-xs font-semibold">KnowsBall</span>
				<span className="ml-auto text-[0.6875rem] opacity-60">now</span>
			</div>
			<div className="mt-2.5 flex flex-col gap-0.5">
				<span className="text-[0.8125rem] leading-snug font-semibold">
					{headline}
				</span>
				<span className="text-xs leading-snug opacity-75">{detail}</span>
			</div>
			{previewApps.map((app) => (
				<div
					className="mt-2 flex items-center gap-2 rounded-xl bg-white/12 px-2.5 py-1.5"
					key={app.id}
				>
					<AppMark app={app} size="sm" />
					<span className="text-xs font-medium">Open {app.name}</span>
					<ArrowUpRight className="ml-auto size-3.5 opacity-60" />
				</div>
			))}
		</div>
	);
}
